import { useState } from "react";
import { Button, EmptyState, Modal, Spinner } from "@cntrl/ui";
import { ApiError } from "../../app/hooks/apiError.js";
import { useSharedShow } from "../../app/hooks/useShowShares.js";
import { ShowPreview } from "./ShowPreview.js";
import { ShowEditorModal } from "./ShowEditorModal.js";
import styles from "./GuestShowModal.module.scss";

export interface GuestShowModalProps {
  /** The inviter-band show id the guest holds an invited/accepted share to. */
  showId: string;
  onClose: () => void;
}

const noop = () => {};

/**
 * The entry point for a guest into a shared show (ART-165). It fetches the guest's read-only matrix ONCE and
 * drives the owner's own PREVIEW → EDIT shape:
 *
 *  • by default it shows the SAME {@link ShowPreview} the owner uses, in `guest` mode — the read-only VIEW
 *    modal (point 1: a guest opens the inviter's view modal, never a bespoke one and never straight to edit);
 *  • its Edit button (a `schedule:write` member only) swaps to the SAME `ShowEditorModal`/`ShowWizard` the
 *    owner uses, in `guest` mode: the inviter's basics/lineup/run-of-show render as disabled editor fields and
 *    the guest's own per-band sections are editable (point 3).
 *
 * A read-only guest member never reaches the editor (the preview withholds Edit), so the old fully view-only
 * editor mode is gone (point 1a). Cancel/Close from the editor returns to the view rather than dismissing
 * outright — the owner's preview→edit→preview shape.
 */
export function GuestShowModal({ showId, onClose }: GuestShowModalProps) {
  const q = useSharedShow(showId);
  const [editing, setEditing] = useState(false);
  const [step, setStep] = useState(1);

  // While the matrix loads (or on a 404 = withdrawn/unfriended), a light modal — neither the view nor the
  // editor mounts until the show is in hand.
  if (q.isLoading || q.isError || !q.data) {
    return (
      <Modal title="Show" size="lg" onClose={onClose}>
        {q.isLoading ? (
          <div className={styles.center} role="status" aria-live="polite">
            <Spinner size={22} label="Loading show…" />
          </div>
        ) : (
          <div className={styles.center}>
            {q.error instanceof ApiError && q.error.status === 404 ? (
              <EmptyState title="Show not available" description="This share was withdrawn, or you're no longer friends." />
            ) : (
              <EmptyState
                title="Couldn't load this show"
                description={q.error instanceof Error ? q.error.message : "Please try again."}
                action={<Button onClick={() => void q.refetch()}>Retry</Button>}
              />
            )}
          </div>
        )}
      </Modal>
    );
  }

  if (editing) {
    return (
      <ShowEditorModal
        showId={showId}
        guest={q.data}
        step={step}
        onStep={setStep}
        onCreated={noop}
        onShowCreated={noop}
        // Cancel / Escape / a clean save all return to the read-only view — the show is the guest's to read,
        // and only their own sections were being edited. Delete cannot happen in guest mode (the footer drops
        // it), so `onDeleted` closing outright is only a defensive path.
        onExit={() => setEditing(false)}
        onDeleted={onClose}
        onSaved={() => setEditing(false)}
      />
    );
  }

  // The SAME view-show modal the owner uses, read-only from the matrix. Its Edit button (schedule:write) opens
  // the editor above; onOpenGuests is an owner-only affordance the guest footer never renders.
  return (
    <ShowPreview
      showId={showId}
      guest={q.data}
      onEdit={() => setEditing(true)}
      onOpenGuests={noop}
      onBack={onClose}
    />
  );
}
